import React from 'react';
import {
  HStack,
  Button,
  Text,
  Flex,
  Box,
  VisuallyHidden,
} from '@chakra-ui/react';
import Icon from '../shared/icons';

interface PaginationProps {
  totalPages: number;
  limit: number;
  offset: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onGoToPage?: (page: number) => void;
  onSetItemsPerPage?: (limit: number) => void;
  totalItems?: number;
  counterPages: number;
  isLoading?: boolean;
  currentPage?: number;
  isFirstPage?: boolean;
  isLastPage?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  limit,
  offset,
  onPrevPage,
  onNextPage,
  onGoToPage,
  totalItems: providedTotalItems,
  currentPage: providedCurrentPage,
  isFirstPage: providedIsFirstPage,
  isLastPage: providedIsLastPage,
}) => {
  const currentPage = providedCurrentPage ?? Math.floor(offset / limit) + 1;

  const isFirstPage = providedIsFirstPage ?? offset === 0;
  const isLastPage = providedIsLastPage ?? currentPage >= totalPages;

  const totalItems =
    providedTotalItems !== undefined ? providedTotalItems : totalPages * limit;

  const fromItem = totalItems === 0 ? 0 : offset + 1;
  const toItem = Math.min(offset + limit, totalItems);

  const generatePageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(startPage + 4, totalPages);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <Flex
      w="full"
      as="nav"
      aria-label="Paginación"
      align="center"
      justify="center"
      mt={8}
      gap={2}
    >
      <HStack gap={2}>
        <Button
          aria-label="Primera página"
          size="sm"
          variant="ghost"
          onClick={() => onGoToPage && onGoToPage(1)}
          opacity={isFirstPage ? 0.5 : 1}
        >
          <Box>
            <Icon icon="chevronLeft" size="xl" />
          </Box>
        </Button>

        <Button
          aria-label="Página anterior"
          size="sm"
          variant="ghost"
          onClick={onPrevPage}
          opacity={isFirstPage ? 0.5 : 1}
        >
          <Box>
            <Icon icon="chevronLeft" size="xl" />
          </Box>
        </Button>

        <Text fontSize="sm" color="gray.600" mx={2} whiteSpace="nowrap">
          {fromItem}-{toItem} de {totalItems}
        </Text>

        {pageNumbers.map((page) => (
          <Button
            key={page}
            size="sm"
            minW="8"
            aria-label={`Ir a la página ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
            bg={page === currentPage ? 'black' : 'gray.100'}
            color={page === currentPage ? 'white' : 'gray.800'}
            _hover={{ bg: page === currentPage ? 'black' : 'gray.200' }}
            borderRadius="md"
            onClick={() => onGoToPage && onGoToPage(page)}
          >
            {page}
          </Button>
        ))}

        <Button
          aria-label="Página siguiente"
          size="sm"
          variant="ghost"
          onClick={onNextPage}
          opacity={isLastPage ? 0.5 : 1}
        >
          <Box>
            <Icon icon="chevronRight" size="xl" />
          </Box>
        </Button>

        <Button
          aria-label="Última página"
          size="sm"
          variant="ghost"
          onClick={() => onGoToPage && onGoToPage(totalPages)}
          opacity={isLastPage ? 0.5 : 1}
        >
          <Box>
            <Icon icon="chevronRight" size="lg" />
          </Box>
        </Button>
      </HStack>

      <VisuallyHidden>
        <div role="status" aria-live="polite">
          Mostrando resultados {fromItem} a {toItem} de {totalItems}
        </div>
      </VisuallyHidden>
    </Flex>
  );
};

export default Pagination;